import { useMemo } from "react";
import { useOrgaConfiguration } from "./useOrgaConfiguration";
import { PAYMENT_PROVIDERS } from "@/shared/config/paymentProviders";
import { OrgaConfiguration } from "@/models/orgaConfiguration";

/**
 * Calcule l'état de complétude d'une organisation à partir de sa configuration.
 * Utilisé par le hook useOrgaCompletion et directement dans certaines pages.
 */
export const getOrgaCompletionStatus = (configuration: OrgaConfiguration | null) => {
  if (!configuration) {
    return {
      hasName: false,
      hasLegalInfo: false,
      isGeneralReady: false,
      isStripeValid: false,
      hasShippingMethods: false,
      hasSenderAddress: false,
      hasContactConfigured: false,
      isPortalReady: false,
    };
  }

  // --- Stripe / Paiement ---
  const stripeCfg = configuration.stripeConfig;

  // Determine active mode: root level takes priority, fallback to stripeConfig for backward compatibility
  const activeMode = configuration.paymentMode && configuration.paymentMode !== 'none'
    ? configuration.paymentMode
    : (stripeCfg?.paymentMode || 'stripe_connect');

  // Find provider definition
  const activeProvider = PAYMENT_PROVIDERS.find(p => p.id === activeMode);

  // Check validity using the provider's logic
  // On vérifie UNIQUEMENT le provider actif (sélectionné)
  const isStripeValid = activeProvider ? activeProvider.configCheck(stripeCfg, configuration) ?? false : false;

  // --- Livraison (shippingConfig) ---
  const hasShippingMethods = Boolean(
    configuration.shippingConfig?.methods?.some(m => m.isActive),
  );

  const senderAddr = configuration.shippingConfig?.senderAddress;
  const hasSenderAddress = Boolean(
    senderAddr?.name?.trim() &&
    senderAddr?.address_line_1?.trim() &&
    senderAddr?.postal_code?.trim() &&
    senderAddr?.city?.trim() &&
    senderAddr?.country_code?.trim()
  );

  // --- Informations Légales ---
  const hasLegalInfo = Boolean(
    configuration.legalInfo?.companyName?.trim() &&
    configuration.legalInfo?.legalId?.trim() &&
    (configuration.legalInfo?.address?.street?.trim() || configuration.address?.street?.trim()) &&
    (configuration.legalInfo?.address?.city?.trim() || configuration.address?.city?.trim()) &&
    (configuration.legalInfo?.address?.postalCode?.trim() || configuration.address?.postalCode?.trim())
  );

  // --- Nom de l'organisation (requis) ---
  const hasName = Boolean(configuration.name && configuration.name.trim().length > 0);

  // --- État Général (Prêt ?) ---
  const isGeneralReady = hasName && hasLegalInfo;

  // --- Contacts : au moins 1 interne valide + 1 SAV valide ---
  const hasInternal = (configuration.contacts?.length ?? 0) > 0 &&
    configuration.contacts!.some((c: any) => c.name && c.name.trim() !== '' && c.email && c.email.trim() !== '');

  const sav: any = configuration.afterSalesService;
  const hasSavArray = Array.isArray(sav?.contacts) &&
    sav.contacts.length > 0 &&
    sav.contacts.some((c: any) => c.value && c.value.trim() !== '');

  const hasLegacySav = Boolean(sav?.email || sav?.phone || sav?.whatsapp);
  const hasContactConfigured = hasInternal && (hasSavArray || hasLegacySav);

  // --- Portail prêt ? ---
  // Conditions d'activation : isActive + nom + Stripe + livraison + contacts + adresse expéditeur
  const isPortalReady = Boolean(
    configuration.isActive &&
    isGeneralReady &&
    isStripeValid &&
    hasShippingMethods &&
    hasContactConfigured &&
    hasSenderAddress,
  );

  return {
    hasName,
    hasLegalInfo,
    isGeneralReady,
    isStripeValid,
    hasShippingMethods,
    hasSenderAddress,
    hasContactConfigured,
    isPortalReady,
    activeMode,
  };
};

/**
 * useOrgaCompletion
 * Centralise l'ensemble de la logique de complétude de la configuration d'une organisation.
 */
export const useOrgaCompletion = (
  organizationIdOverride?: string,
  options: { once?: boolean } = {}
) => {
  const { configuration, loading, error } = useOrgaConfiguration(organizationIdOverride, options);

  const status = useMemo(() => {
    if (loading) {
      return {
        hasName: false,
        hasLegalInfo: false,
        isGeneralReady: false,
        isStripeValid: false,
        hasShippingMethods: false,
        hasSenderAddress: false,
        hasContactConfigured: false,
        isPortalReady: false,
        activeMode: undefined,
      };
    }
    return getOrgaCompletionStatus(configuration || null);
  }, [configuration, loading]);

  return {
    ...status,
    loading,
    error,
    configuration,
  } as const;
};
